import React, { useState, useContext } from 'react'
import { connect } from 'react-redux'
import styles from './Challenges.scss'
import { ChallengeCard } from 'features/challenges/ChallengeCard'
import { OrgContext } from 'features/organizations/OrgContext'
import { StandardLayout } from 'common/layouts/StandardLayout'
import { Loadable } from 'common/Loadable'
import { PopoverFilter } from 'common/PopoverFilter'
import { PopoverMenuItem, PopoverMenuHeading } from 'common/styleguide/PopoverMenu'
import popoverMenuStyles from 'common/styleguide/PopoverMenu/PopoverMenu.scss'
import { RadioGroup } from 'common/styleguide/RadioGroup'
import { CTA } from 'common/styleguide/CTA'
import { getCurrentUser, promiseMapAll } from 'lib/utils'
import { c } from 'lib/textUtils'
import { useFormatMessage, featureNames, commonNouns, privacyLevels } from 'lib/localization'
import { goalActions, goalSelectors } from 'data/resources/goals'
import { goalParticipantSelectors } from 'data/resources/goalParticipants'
import { defineMessages } from 'react-intl'
import { Map } from 'immutable'
import moment from 'moment'

export const messages = defineMessages({
  loadingText: {
    id: 'Challenges.loadingText',
    defaultMessage: 'Loading your Challenges' // lowercase to overlap
  },
  all: {
    id: 'Challenges.all',
    defaultMessage: 'all' // lowercase to overlap
  },
  allChallenges: {
    id: 'Challenges.allChallenges',
    defaultMessage: 'All Challenges'
  },
  current: {
    id: 'Challenges.current',
    defaultMessage: 'Current'
  },
  currentChallenges: {
    id: 'Challenges.currentChallenges',
    defaultMessage: 'Current Challenges'
  },
  past: {
    id: 'Challenges.past',
    defaultMessage: 'Past'
  },
  pastChallenges: {
    id: 'Challenges.pastChallenges',
    defaultMessage: 'Past Challenges'
  },
  newChallenge: {
    id: 'Challenges.newChallenge',
    defaultMessage: 'New Challenge'
  },
  noCurrent: {
    id: 'Challenges.noCurrent',
    defaultMessage: 'You are not currently participating in any Challenges'
  },
  noPast: {
    id: 'Challenges.noPast',
    defaultMessage: 'You have no past Challenges'
  },
  nothingToShow: {
    id: 'Challenges.nothingToShow',
    defaultMessage: 'No Challenges matched your filters'
  }
})

const clubFilterOptions = fm => Map({
  all: c(fm(messages.all)),
  'public': fm(privacyLevels.public),
  members: fm(privacyLevels.members),
  managers: fm(privacyLevels.managers)
})

const clubFilterDateOptions = fm => Map({
  current: fm(messages.current),
  past: fm(messages.past)
})

const userFilterOptions = fm => Map({
  all: c(fm(messages.all)),
  current: fm(messages.current),
  past: fm(messages.past)
})

const labelOptions = fm => Map({
  all: fm(messages.allChallenges),
  current: fm(messages.currentChallenges),
  past: fm(messages.pastChallenges),
  'public': fm(privacyLevels.public),
  members: fm(privacyLevels.members),
  managers: fm(privacyLevels.managers)
})

const filteredGoals = (goals, filter, dateFilter, currentMember) => {
  if (currentMember) {
    let filtered
    if (filter === 'all') {
      filtered = goals
    }
    if (filter === 'public') {
      filtered = goals.filter(g => g.visibility === 0)
    }
    if (filter === 'members') {
      filtered = goals.filter(g => g.visibility === 2)
    }
    if (filter === 'managers') {
      filtered = goals.filter(g => g.visibility === 1)
    }

    const now = moment()
    if (dateFilter === 'past') {
      return filtered.filter(g => moment(g.endsOn).isBefore(now))
    } else {
      return filtered.filter(g => !moment(g.endsOn).isBefore(now))
    }
  } else {
    // homepage index-- only care about dates here
    if (filter === 'all') {
      return goals
    }
    const now = moment()
    if (filter === 'current') {
      return goals.filter(g => moment(g.startsOn).isBefore(now) && moment(g.endsOn).isAfter(now))
    }
    if (filter === 'past') {
      return goals.filter(g => moment(g.endsOn).isBefore(now))
    }
    console.warn(`unexpected filter value ${dateFilter}`)
    return goals
  }
}

export const _Challenges = ({ goals, onNewChallengeClick, fm }) => {
  const { isCurrentClub, currentMember } = useContext(OrgContext)
  const [filter, setFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState(currentMember ? 'current' : 'all')

  const filtered = filteredGoals(goals, filter, dateFilter, currentMember)
  return (
    <>
      <h2 className={styles.challengesHeader} data-heading-alt>
        {fm(featureNames.challenges)}
      </h2>

      <div className={styles.challengeTools}>
        <PopoverFilter
          hidePopoverLabel
          noPadding
          label={labelOptions(fm).get(filter)}
        >
          {currentMember
            ? <ul className={popoverMenuStyles.PopoverMenu}>
              <PopoverMenuHeading>{fm(privacyLevels.privacy)}</PopoverMenuHeading>
              <PopoverMenuItem>
                <RadioGroup
                  style={{ margin: '0 0' }}
                  horizontal
                  options={clubFilterOptions(fm)}
                  selected={filter}
                  radioClass={styles.radioClass}
                  onChange={setFilter} />
              </PopoverMenuItem>
              <PopoverMenuHeading>{c(fm(commonNouns.date))}</PopoverMenuHeading>
              <PopoverMenuItem>
                <RadioGroup
                  style={{ margin: '0 0' }}
                  horizontal
                  options={clubFilterDateOptions(fm)}
                  selected={dateFilter}
                  radioClass={styles.radioClass}
                  onChange={setDateFilter} />
              </PopoverMenuItem>
            </ul>
            : <ul className={popoverMenuStyles.PopoverMenu}>
              <PopoverMenuItem>
                <RadioGroup
                  style={{ margin: '0 0' }}
                  horizontal
                  options={userFilterOptions(fm)}
                  selected={filter}
                  radioClass={styles.radioClass}
                  onChange={setFilter} />
              </PopoverMenuItem>
            </ul>
          }
        </PopoverFilter>
        {isCurrentClub && <div style={{ display: 'flex' }}>
          <CTA
            secondary
            margins={false}
            className={styles.newCTA}
            onClick={onNewChallengeClick}>
            {fm(messages.newChallenge)}
          </CTA>
        </div>}
      </div>

      {filtered?.size > 0
        ? <div className={styles.cards}>
          {filtered.map(g => <ChallengeCard key={g.id} g={g} fm={fm} />).toIndexedSeq()}
        </div>
        : <div className={styles.noChallenges}>
          {currentMember
            ? fm(messages.nothingToShow)
            : fm(filter === 'current' ? messages.noCurrent : messages.noPast)}
        </div>}
    </>
  )
}

export const _UserChallenges = props => {
  return <StandardLayout greyBg>
    <OrgContext.Provider value={{}}>
      <div className={styles.wrap}>
        <_Challenges {...props} />
      </div>
    </OrgContext.Provider>
  </StandardLayout>
}

const loadInitialResources = ({ loadGoals }) => {
  return promiseMapAll({
    _: loadGoals({ scope: 'challenges' })
  })
}

const LoadableUserChallenges = (props) => {
  const fm = useFormatMessage()
  return <Loadable
    {...props}
    fm={fm}
    component={_UserChallenges}
    loadingText={fm(messages.loadingText)}
    resourceLoaded={() => loadInitialResources(props)} />
}

const mapStateToProps = (state, ownProps) => {
  const currentUser = getCurrentUser()
  return {
    goals: goalSelectors.findByIds(
      state,
      goalParticipantSelectors
        .allForUser(state, currentUser.id)
        .map(gp => gp.goalId)
    ),
    currentUser
  }
}

const mapDispatchToProps = {
  loadGoals: goalActions.list
}

export const UserChallenges = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableUserChallenges)
